import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { get } from "@/utils/service";
import { darkInput } from "./FloatingDrawer";

export type Picked = { id: string; label: string; raw?: Record<string, unknown> } | null;

const LABEL: Record<string, (r: Record<string, any>) => string> = {
  accounts: (r) => r.name,
  contacts: (r) => [r.name, r.account?.name].filter(Boolean).join(" · "),
  deals: (r) => [r.title, r.account?.name].filter(Boolean).join(" · "),
  products: (r) => [r.name, r.sku].filter(Boolean).join(" · "),
  leads: (r) => r.name,
  contracts: (r) => [r.number, r.title || r.service_type, r.account?.name].filter(Boolean).join(" · "),
};

/** Search-as-you-type picker over a list endpoint; only returns records the user may see. */
export default function RecordSelect({ kind, value, onChange, placeholder, params, light }: {
  kind: "accounts" | "contacts" | "deals" | "products" | "leads" | "contracts";
  value: Picked;
  onChange: (v: Picked) => void;
  placeholder?: string;
  params?: Record<string, string>;
  light?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [rows, setRows] = useState<Picked[]>([]);
  const box = useRef<HTMLDivElement>(null);
  const paramsKey = JSON.stringify(params ?? {});
  const inputClass = light ? "w-full px-3 py-2 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary" : darkInput;

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const q = new URLSearchParams({ search: term, page_size: "8", ...(JSON.parse(paramsKey) as Record<string, string>) });
      get<{ data: Record<string, unknown>[] }>(`/${kind}?${q}`)
        .then((r) => setRows(r.data.map((x) => ({ id: String(x.id), label: LABEL[kind](x), raw: x }))))
        .catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [open, term, kind, paramsKey]);

  useEffect(() => {
    const close = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (value && !open) {
    return (
      <div className={`${inputClass} flex items-center justify-between gap-2`}>
        <span className="truncate">{value.label}</span>
        <button type="button" aria-label="Clear" onClick={() => onChange(null)} className="text-gray-400 hover:text-gray-200"><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }

  return (
    <div ref={box} className="relative">
      <input className={inputClass} value={term} placeholder={placeholder ?? "Type to search…"} onFocus={() => setOpen(true)} onChange={(e) => { setTerm(e.target.value); setOpen(true); }} />
      {open && (
        <div className={`absolute z-[100001] mt-1 max-h-56 w-full overflow-y-auto rounded-lg border shadow-xl ${light ? "border-gray-200 bg-white" : "border-gray-700 bg-gray-900"}`}>
          {rows.length === 0 ? (
            <p className={`px-3 py-2 text-xs ${light ? "text-gray-500" : "text-gray-400"}`}>No matches</p>
          ) : rows.map((r) => r && (
            <button key={r.id} type="button" onClick={() => { onChange(r); setOpen(false); setTerm(""); }} className={`block w-full px-3 py-2 text-left text-xs ${light ? "text-gray-800 hover:bg-gray-50" : "text-white hover:bg-gray-800"}`}>
              {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
